import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";

const outstanding = [
  { initials: "MH", role: "Pharmacy Technician", days: 3, tone: "amber" },
  { initials: "LJ", role: "Clinical Nurse", days: 5, tone: "amber" },
  { initials: "RO", role: "Cultivation Lead", days: 7, tone: "rose" },
  { initials: "SS", role: "QA Officer", days: 2, tone: "amber" },
];

function Donut({ value = 84 }) {
  const data = [{ name: "v", value, fill: "url(#emGrad)" }];
  return (
    <div className="relative h-[168px] w-[168px]">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          innerRadius="78%"
          outerRadius="100%"
          barSize={12}
          data={data}
          startAngle={90}
          endAngle={-270}
        >
          <defs>
            <linearGradient id="emGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          <PolarAngleAxis
            type="number"
            domain={[0, 100]}
            angleAxisId={0}
            tick={false}
          />
          <RadialBar
            background={{ fill: "rgba(255,255,255,0.05)" }}
            dataKey="value"
            cornerRadius={30}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-[42px] leading-none tracking-tighter text-white">
          {value}
          <span className="text-emerald-400">%</span>
        </div>
        <div className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Acknowledged
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta, tone = "em" }) {
  const toneCls =
    tone === "em"
      ? "text-emerald-400"
      : tone === "amber"
        ? "text-amber-400"
        : "text-rose-400";
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-sm">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-[26px] leading-none tracking-tight text-white">
          {value}
        </span>
        <span className={`text-[11px] font-medium ${toneCls}`}>{delta}</span>
      </div>
    </div>
  );
}

export default function DashboardMock() {
  return (
    <div
      data-testid="dashboard-mock"
      className="mock-chrome relative w-full max-w-[560px] lg:max-w-none mx-auto"
    >
      {/* chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
        <span className="h-2.5 w-2.5 rounded-full dot-r" />
        <span className="h-2.5 w-2.5 rounded-full dot-y" />
        <span className="h-2.5 w-2.5 rounded-full dot-g" />
        <div className="ml-4 flex-1 flex justify-center">
          <div className="flex items-center gap-2 rounded-md bg-black/40 border border-white/[0.05] px-3 py-1 text-[11px] text-slate-400">
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            app.cannicomply.co.uk / dashboard
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-400/90 flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </div>
      </div>

      {/* Body */}
      <div className="p-6 md:p-8">
        {/* Header row */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Acknowledgement Overview
            </div>
            <div className="mt-1 font-display text-xl text-white tracking-tight">
              Controlled Drug Handling SOP v6.2
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Issued 12 Feb 2026 · Due within 14 days
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1 text-[11px] text-slate-300">
              All departments
            </div>
            <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-300">
              This month
            </div>
          </div>
        </div>

        {/* Metrics row */}
        <div className="mt-6 grid grid-cols-12 gap-4">
          <div className="col-span-12 rounded-xl border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent p-5 flex items-center gap-5">
            <Donut value={84} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                Compliance velocity
              </div>
              <div className="mt-1 text-[13px] text-slate-300 leading-relaxed">
                <span className="text-emerald-400 font-medium">+12 pts</span>{" "}
                vs previous SOP cycle. On track to reach 100 percent before
                the due date.
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Updated 2 minutes ago
              </div>
            </div>
          </div>

          <div className="col-span-12 grid grid-cols-3 gap-3">
            <KpiCard label="Acknowledged" value="248" delta="↑ 12" tone="em" />
            <KpiCard label="Pending" value="18" delta="↓ 4" tone="amber" />
            <KpiCard label="Overdue" value="4" delta="0" tone="rose" />

            {/* Outstanding list */}
            <div className="col-span-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Outstanding actions
                </div>
                <div className="text-[10px] text-slate-500">4 people</div>
              </div>
              <ul className="mt-3 divide-y divide-white/[0.04]">
                {outstanding.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 border border-white/10 flex items-center justify-center text-[10px] text-slate-300">
                        {p.initials}
                      </div>
                      <div>
                        <div className="text-sm text-white">{p.role}</div>
                        <div className="text-[11px] text-slate-500">
                          Staff · ID {p.initials}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full border ${
                        p.tone === "rose"
                          ? "text-rose-300 border-rose-500/30 bg-rose-500/10"
                          : "text-amber-300 border-amber-500/30 bg-amber-500/10"
                      }`}
                    >
                      {p.days}d overdue
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Floating toast */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="hidden 2xl:flex absolute -right-8 top-24 items-center gap-3 rounded-xl border border-emerald-500/30 bg-[#0a1f18]/90 backdrop-blur px-4 py-3 shadow-[0_20px_60px_-20px_rgba(16,185,129,0.4)] floaty"
        data-testid="floating-toast"
      >
        <div className="h-8 w-8 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#34d399"
            strokeWidth="3"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div className="text-xs">
          <div className="text-white font-medium">Acknowledgement recorded</div>
          <div className="text-slate-400">Controlled Drug Handling · 74%</div>
        </div>
      </motion.div>

      {/* Floating stat */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9, duration: 0.5 }}
        className="hidden 2xl:block absolute -left-16 top-40 rounded-xl border border-white/10 bg-[#0a1325]/90 backdrop-blur px-4 py-3 shadow-2xl floaty"
        style={{ animationDelay: "1.2s" }}
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Certificates issued
        </div>
        <div className="mt-1 font-display text-2xl text-white tracking-tight">
          1,284{" "}
          <span className="text-emerald-400 text-xs align-middle">this quarter</span>
        </div>
      </motion.div>
    </div>
  );
}
